<template>
    <div class="aboutPage">
        <section class="aboutHero">
            <div class="eyebrow">About Us</div>
            <h1>We Will Get You Ready for Sunrise 2027</h1>
            <p class="heroLead">
                A new approach for GS1 Sunrise 2027: brands need to support both traditional UPC
                scanning and QR code experiences. QR Code Experts combines both into a single code
                workflow so packaging, product, and marketing teams can move faster without losing
                operational control.
            </p>
            <div class="heroHighlights">
                <span>Save label space</span>
                <span>Change digital content without reprinting</span>
                <span>Reduce packaging costs</span>
                <span>Increase flexibility for marketing and product information</span>
            </div>
        </section>

        <section class="aboutCard">
            <h2>Print Once: Changes Forever.</h2>
            <p>
                We empower businesses to create smarter product experiences. The platform lets teams
                create, manage, and publish intelligent QR codes with dynamic redirects, analytics,
                and UPC barcode integration.
            </p>
            <p class="aboutQuote">"PRINT once. You can REDIRECT forever."</p>
        </section>

        <section class="aboutCard">
            <h2>What the Site Makes Available</h2>
            <p>
                Authorized users can create QR-backed shortcodes that resolve through
                <strong>QREXL.INK</strong>, review existing code records, manage ownership, and
                maintain a working inventory of established codes. The primary purpose of the site is
                to keep QR code operations in one place so teams can generate, review, and update
                active codes without losing track of what is in use.
            </p>
            <p>
                Redirect control is part of the workflow, allowing the destination behind an existing
                code to change without replacing the printed QR image. For product and SKU use cases,
                the site can also attach GTIN/UPC barcode data and produce barcode-related assets, but
                those capabilities remain secondary to the core QR code management workflow.
            </p>
            <p class="aboutQuote">"PRINT once, you can ORGANIZE forever."</p>
        </section>

        <section class="aboutGrid">
            <article class="featureCard">
                <h3>Code Management</h3>
                <p>
                    Take full control of your QR code ecosystem. Create new QR codes, assign or
                    transfer ownership, review code history, organize records by user, and keep a
                    complete inventory of active codes.
                </p>
                <p class="featureQuote">"PRINT once, you can MANAGE forever."</p>
            </article>
            <article class="featureCard">
                <h3>Redirect Support</h3>
                <p>
                    Keep every printed QR code useful. Update the destination behind an existing code
                    instantly without reprinting anything already in circulation.
                </p>
                <p class="featureQuote">"PRINT once. The code will be USEFUL forever."</p>
            </article>
            <article class="featureCard">
                <h3>Analytics Support</h3>
                <p>
                    Track redirect traffic for each code with scan activity over time, geographic
                    summaries, and usage trends that help teams make better decisions.
                </p>
                <p class="featureQuote">"PRINT once: you can ANALYZE forever".</p>
            </article>
            <article class="featureCard">
                <h3>Barcode Assets</h3>
                <p>
                    Extend the QR workflow with integrated GTIN/UPC barcode support. Generate
                    standard barcode assets or combined QR/GTIN labels to connect product
                    identification with digital content.
                </p>
                <p class="featureQuote">"PRINT once. IDENTIFY forever."</p>
            </article>
        </section>

        <section class="aboutCard">
            <h2>Who This Is For</h2>
            <p>
                Designed for organizations that rely on QR codes in daily operations, including
                operations teams, product managers, manufacturers, packaging departments, marketing
                teams, inventory managers, retail tech firms, label printers, packaging companies,
                and barcode solution providers.
            </p>
            <p>
                Whether managing dozens of codes or millions of products, the platform keeps QR code
                assets organized, secure, and easy to manage throughout their lifecycle.
            </p>
            <p class="aboutQuote">"Print once. SAVE MONEY forever."</p>
        </section>

        <section class="partnerCard">
            <h2>Ideal Partners</h2>
            <div class="partnerGrid">
                <span>Packaging companies</span>
                <span>Retail tech firms</span>
                <span>Label printers</span>
                <span>Barcode solution providers</span>
            </div>
            <p class="closingQuote">"BUY ONCE: SMILE FOREVER"</p>
        </section>
    </div>
</template>

<script>
export default {
    name: 'AboutView',
    // only used in Nav after lazy load.
    display: "About",
}
</script>

<style>
.aboutPage {
    max-width: 980px;
    margin: 24px auto 40px;
    padding: 0 16px;
}

.aboutHero,
.aboutCard,
.featureCard {
    background: var(--qrce-surface);
    border: 1px solid var(--qrce-border);
    box-shadow: var(--qrce-shadow);
}

.aboutHero {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 28px;
    border-radius: 24px;
    background:
        radial-gradient(circle at top right, rgba(29, 78, 216, 0.14), transparent 28%),
        linear-gradient(135deg, #ffffff 0%, #eef4ff 58%, #e4efff 100%);
    text-align: center;
}

.eyebrow {
    display: inline-block;
    margin-bottom: 10px;
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba(29, 78, 216, 0.12);
    color: var(--qrce-primary-600);
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.aboutHero h1 {
    margin: 0 0 12px;
    font-size: clamp(2rem, 4vw, 3rem);
    line-height: 0.98;
    letter-spacing: -0.04em;
}

.aboutHero p,
.aboutCard p,
.featureCard p,
.partnerCard p {
    margin: 0;
    line-height: 1.7;
    color: var(--qrce-muted);
}

.heroLead {
    max-width: 62ch;
    font-size: 1.04rem;
    margin-inline: auto !important;
}

.heroHighlights {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 18px;
    justify-content: center;
    max-width: 980px;
}

.heroHighlights span,
.partnerGrid span {
    display: inline-flex;
    align-items: center;
    min-height: 38px;
    padding: 8px 14px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(15, 23, 42, 0.08);
    color: var(--qrce-text);
    font-size: 0.92rem;
    font-weight: 600;
}

.aboutCard {
    margin-top: 18px;
    padding: 22px;
    border-radius: 20px;
}

.aboutCard h2,
.featureCard h3 {
    margin: 0 0 10px;
    color: var(--qrce-text);
}

.aboutGrid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
    margin-top: 18px;
}

.featureCard {
    padding: 20px;
    border-radius: 18px;
}

.partnerCard {
    margin-top: 18px;
    padding: 24px;
    border-radius: 20px;
    background:
        linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%);
    color: #e2e8f0;
    border: 1px solid rgba(15, 23, 42, 0.18);
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.18);
}

.partnerCard h2,
.partnerCard p {
    color: inherit;
}

.partnerGrid {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 14px;
}

.partnerCard .partnerGrid span {
    background: rgba(255, 255, 255, 0.14);
    border-color: rgba(255, 255, 255, 0.18);
    color: #f8fafc;
}

.aboutQuote,
.featureQuote,
.closingQuote {
    margin-top: 14px !important;
    color: var(--qrce-text) !important;
    font-weight: 700;
    letter-spacing: 0.01em;
}

.closingQuote {
    color: #f8fafc !important;
}

@media (max-width: 819px) {
    .aboutPage {
        margin-top: 16px;
        padding: 0 12px;
    }

    .aboutGrid {
        grid-template-columns: 1fr;
    }

    .aboutHero,
    .aboutCard,
    .featureCard {
        border-radius: 18px;
    }
}
</style>
